import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { SymbolGrid } from "@/components/SymbolGrid";
import { SymbolSearch } from "@/components/SymbolSearch";
import { MySymbolsPanel } from "@/components/MySymbolsPanel";
import { categories } from "@/data/symbols";
import { getCategoryGroups } from "@/data/category-groups";
import { FANCY_STYLE_COUNT } from "@/data/generator-stats.generated";
import { totalSymbols, totalCategories, formatCount, formatCountPlus } from "@/data/stats";

const popular = ["♥","★","→","✓","✦","☆","☀","♫","✿","☂","☃","♛","♡","✨","✈","☎","✉","☯","⚡","♻","☮","☘","♔","⚽","☕","⚓","⌘","⊙","✪","☄","♨","✺","⚜","✧","☾","♬"];

// The flagship tools/feature pages, surfaced at the top of the homepage. Each
// `to` is a real registered route; `font` picks the preview font (text faces
// need the normal sans font, ornamental combos the symbol-rich one).
const FEATURES = [
  { to: "/font-generator", name: "Font Generator", desc: `Type once, copy ${FANCY_STYLE_COUNT} fancy fonts — cursive, bold, gothic, bubble, aesthetic & more.`, preview: "𝒞𝑜𝑜𝓁 𝕋𝕖𝕩𝕥", font: "font-sans" },
  { to: "/kaomoji", name: "Kaomoji & Text Faces", desc: "200+ Japanese emoticons by mood — happy, angry, love, shrug & table flip.", preview: "(◕‿◕)", font: "font-sans" },
  { to: "/lenny-faces", name: "Lenny Face", desc: "The classic ( ͡° ͜ʖ ͡°) and dozens of variations, ready to copy.", preview: "( ͡° ͜ʖ ͡°)", font: "font-sans" },
  { to: "/decorative", name: "Decorative Symbols", desc: "Borders, dividers, loading bars & aesthetic combos for bios and posts.", preview: "═══ ✦ ═══", font: "font-combo" },
  { to: "/draw", name: "Draw to Find", desc: "Sketch a symbol with your mouse and we find the closest matches.", preview: "✎ ⌕", font: "font-combo" },
  { to: "/export", name: "Bulk Export", desc: "Download any symbol as a transparent SVG or PNG — and pick its color.", preview: "⬇ SVG · PNG", font: "font-sans" },
] as const;

const faqs = [
  {
    q: "Are these symbols free to use?",
    a: "Yes — every symbol is part of the Unicode standard and free to use in personal and commercial projects, with no attribution required.",
  },
  {
    q: "How do I copy a symbol?",
    a: "Click or tap any tile and the symbol is instantly placed on your clipboard. No popups, no confirmations, no signup.",
  },
  {
    q: "Where can I paste them?",
    a: "Anywhere that supports Unicode text — Twitter, Instagram, TikTok bios, Word and Google Docs, code, design tools, terminal, and most modern apps.",
  },
  {
    q: "Will they display correctly on every device?",
    a: "Most symbols render identically across iOS, Android, Windows and macOS. A small number of emoji-style glyphs may render slightly differently depending on the OS font.",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: `CoolSymbols.cool — Copy ${formatCountPlus(totalSymbols)} cool text symbols` },
      {
        name: "description",
        content:
          "The fastest symbol library on the web. Click any symbol — hearts ♥, stars ★, arrows →, math ∑ — to copy it instantly. Free, no ads, no signup.",
      },
      { property: "og:title", content: "CoolSymbols.cool — Cool text symbols, copy with one click" },
      {
        property: "og:description",
        content: "Hearts, stars, arrows, math, currency, zodiac and more — one click to copy.",
      },
      { property: "og:url", content: abs("/") },
    ],
    links: [{ rel: "canonical", href: abs("/") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CoolSymbols.cool",
          url: "/",
          potentialAction: {
            "@type": "SearchAction",
            target: "/categories?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  component: Home,
});

function Home() {


  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 pb-10 pt-12 sm:px-6 sm:pt-20">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
              {formatCount(totalSymbols)} symbols across {totalCategories} categories
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-6xl">
              Cool text symbols,
              <br />
              <span className="text-gradient">one click to copy.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Hearts, stars, arrows, math, currency, zodiac and more. Free,
              instant, no signup. The modern replacement for clunky symbol sites.
            </p>
          </div>

          {/* Global search */}
          <div className="mx-auto mt-8 max-w-2xl">
            <SymbolSearch />
          </div>

          {/* Quick category chips */}
          <nav aria-label="Browse categories" className="mx-auto mt-6 max-w-3xl">
            <ul className="flex flex-wrap items-center justify-center gap-2">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/categories/$slug"
                    params={{ slug: c.slug }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/40 px-3 py-1.5 text-xs text-foreground/85 transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
                  >
                    <span aria-hidden="true">{c.emoji}</span>
                    <span>{c.name.replace(" Symbols", "")}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Stats strip */}
          <dl className="mx-auto mt-12 grid max-w-3xl grid-cols-3 gap-3 sm:gap-6">
            {[
              { k: "Symbols", v: formatCount(totalSymbols) },
              { k: "Categories", v: formatCount(totalCategories) },
              { k: "Click to copy", v: "1" },
            ].map((s) => (
              <div key={s.k} className="glass rounded-xl px-4 py-3 text-center">
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{s.k}</dt>
                <dd className="mt-1 font-display text-xl font-bold sm:text-2xl">{s.v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Tools & generators — the flagship feature pages, given top billing. */}
        <section aria-labelledby="tools-heading" className="mx-auto max-w-7xl px-4 pb-12 sm:px-6">
          <div className="mb-4 flex items-end justify-between">
            <h2 id="tools-heading" className="font-display text-2xl font-bold sm:text-3xl">
              Tools &amp; generators
            </h2>
            <Link
              to="/font-generator"
              className="text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Font generator →
            </Link>
          </div>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <li key={f.to}>
                <Link
                  to={f.to}
                  className="group glass flex h-full flex-col rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="mb-3 flex min-h-[3.25rem] items-center justify-center rounded-lg border border-border bg-surface/60 px-3 py-2">
                    <span className={`whitespace-pre text-center text-lg text-foreground/90 ${f.font}`}>{f.preview}</span>
                  </span>
                  <span className="font-display text-lg font-semibold">{f.name}</span>
                  <span className="mt-1 text-sm text-muted-foreground">{f.desc}</span>
                  <span aria-hidden="true" className="mt-3 text-xs text-accent">Open →</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Featured symbols — an editorial selection (not a popularity claim). */}
        <section aria-labelledby="featured-heading" className="mx-auto max-w-7xl px-4 pb-12 sm:px-6">
          <div className="mb-3 flex items-end justify-between">
            <h2
              id="featured-heading"
              className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground"
            >
              Featured symbols
            </h2>
            <Link
              to="/categories"
              className="text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Browse all →
            </Link>
          </div>
          <SymbolGrid symbols={popular} size="lg" />
        </section>

        {/* My symbols — only renders when the local library has any content. */}
        <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6">
          <MySymbolsPanel hideWhenEmpty compact />
        </section>

        {/* Categories */}
        <section aria-labelledby="categories-heading" className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <h2 id="categories-heading" className="font-display text-2xl font-bold sm:text-3xl">
            Browse by category
          </h2>
          <p className="mt-2 text-muted-foreground">
            Every category is its own page, optimized to load fast and rank well.
          </p>
          {getCategoryGroups().map((group) => (
            <div key={group.title} className="mt-10">
              <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {group.title}
              </h3>
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to="/categories/$slug"
                      params={{ slug: c.slug }}
                      className="group glass block rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`${c.name} — ${c.symbols.length} symbols. ${c.tagline}.`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-display text-lg font-semibold">{c.name}</div>
                          <p className="mt-1 text-sm text-muted-foreground">{c.tagline}</p>
                        </div>
                        <div
                          aria-hidden="true"
                          className="text-3xl text-gradient transition-transform group-hover:scale-110"
                        >
                          {c.emoji}
                        </div>
                      </div>
                      <div aria-hidden="true" className="mt-4 flex flex-wrap gap-1.5">
                        {c.symbols.slice(0, 8).map((s, i) => (
                          <span
                            key={i}
                            className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface/60 text-base"
                          >
                            {s}
                          </span>
                        ))}
                        <span className="flex h-8 items-center px-2 text-xs text-muted-foreground">
                          +{c.symbols.length - 8}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Use cases */}
        <section aria-labelledby="use-cases-heading" className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="flex items-end justify-between gap-4">
            <h2 id="use-cases-heading" className="font-display text-2xl font-bold sm:text-3xl">
              Where people use them
            </h2>
            <Link
              to="/use-cases"
              className="shrink-0 text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              All use cases →
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Social bios", d: "Stand out on Twitter, Instagram, TikTok and LinkedIn.", e: "♥" },
              { t: "Docs & decks", d: "Drop arrows, checks and bullets into Word, Notion and Google Docs.", e: "→" },
              { t: "Design & UI", d: "Reach for ornaments and dividers in Figma, Framer and CSS.", e: "✦" },
              { t: "Code & terminal", d: "Use math, logic and Greek letters in comments and READMEs.", e: "∑" },
            ].map((u) => (
              <div key={u.t} className="glass rounded-2xl p-5">
                <div aria-hidden="true" className="text-3xl text-gradient">{u.e}</div>
                <h3 className="mt-3 font-display text-base font-semibold">{u.t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{u.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why */}
        <section aria-labelledby="why-heading" className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <h2 id="why-heading" className="sr-only">Why CoolSymbols.cool</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { t: "Instant copy", d: "Click any symbol — it's on your clipboard in milliseconds. No popups, no confirmations.", e: "⚡" },
              { t: "Genuinely fast", d: "Server-rendered, statically optimized, zero tracking scripts. Loads on any device.", e: "✦" },
              { t: "Works everywhere", d: "All symbols are standard Unicode — paste into Twitter, Instagram, Word, code, anywhere.", e: "✓" },
            ].map((f) => (
              <div key={f.t} className="glass rounded-2xl p-6">
                <div aria-hidden="true" className="text-3xl text-gradient">{f.e}</div>
                <h3 className="mt-3 font-display text-lg font-semibold">{f.t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-heading" className="mx-auto max-w-3xl px-4 pb-20 pt-8 sm:px-6">
          <h2 id="faq-heading" className="font-display text-2xl font-bold sm:text-3xl">
            Frequently asked
          </h2>
          <dl className="mt-6 space-y-3">
            {faqs.map((f) => (
              <div key={f.q} className="glass rounded-xl p-4">
                <dt className="font-semibold">{f.q}</dt>
                <dd className="mt-1 text-sm text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
